import { useState, useEffect, useCallback } from "react";

const COMPARE_KEY = "ef_compare_list";
const MAX_COMPARE = 4;

function getCompareList() {
  try {
    const raw = localStorage.getItem(COMPARE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function setCompareList(list) {
  localStorage.setItem(COMPARE_KEY, JSON.stringify(list));
}

/**
 * Custom hook for product comparison functionality.
 * Stores up to 4 product slugs in localStorage and
 * keeps all mounted components in sync via the "storage" event.
 */
export function useCompare() {
  const [compareList, setLocalList] = useState(getCompareList);

  // Sync across tabs / components that re-mount
  useEffect(() => {
    const handler = () => setLocalList(getCompareList());
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const addToCompare = useCallback((slug) => {
    const current = getCompareList();
    if (current.includes(slug)) return { ok: false, reason: "already" };
    if (current.length >= MAX_COMPARE) return { ok: false, reason: "full" };
    const next = [...current, slug];
    setCompareList(next);
    setLocalList(next);
    // dispatch a storage event so sibling components update
    window.dispatchEvent(new Event("storage"));
    return { ok: true };
  }, []);

  const removeFromCompare = useCallback((slug) => {
    const current = getCompareList();
    const next = current.filter((s) => s !== slug);
    setCompareList(next);
    setLocalList(next);
    window.dispatchEvent(new Event("storage"));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareList([]);
    setLocalList([]);
    window.dispatchEvent(new Event("storage"));
  }, []);

  const isInCompare = useCallback(
    (slug) => compareList.includes(slug),
    [compareList]
  );

  return {
    compareList,
    addToCompare,
    removeFromCompare,
    clearCompare,
    isInCompare,
    isFull: compareList.length >= MAX_COMPARE,
  };
}
